import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import './AboutPage.css';
import AbhiPhoto from '../media/Abhi2.jpg';
import RequestAppraisalOverlay from './RequestAppraisalOverlay';



const AboutPage = () => {

  const navigate = useNavigate();
  const [pageLoaded, setPageLoaded] = useState(false);
  const [isAppraisalOverlayOpen, setAppraisalOverlayOpen] = useState(false);
  const sectionRefs = useRef({});
  const observerRef = useRef(null);

  const openAppraisalOverlay = () => setAppraisalOverlayOpen(true);
  const closeAppraisalOverlay = () => setAppraisalOverlayOpen(false);

  // --- Animation Hooks (Copied from HomePage) ---

  // Page load animation trigger
  useEffect(() => {
    const timer = setTimeout(() => {
      setPageLoaded(true);
    }, 100);
    return () => clearTimeout(timer);
  }, []);

  // IntersectionObserver for fade-in animations
  useEffect(() => {
    observerRef.current = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
          }
        });
      },
      { threshold: 0.1, rootMargin: '0px 0px -50px 0px' }
    );

    const refs = sectionRefs.current;

    // Observe all refs when observer is created
    Object.values(refs).forEach(ref => {
      if(ref && observerRef.current) {
        observerRef.current.observe(ref);
      }
    });

    return () => {
      // Unobserve all refs on cleanup
      Object.values(refs).forEach(ref => {
        if(ref && observerRef.current) {
          observerRef.current.unobserve(ref);
        }
      });
    };
  }, []); // Run this effect once on mount

  // Helper function to assign refs to the observer
  const setSectionRef = (id) => (el) => {
    if (el) {
      sectionRefs.current[id] = el;
      if (observerRef.current) {
        observerRef.current.observe(el);
      }
    }
  };

  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="page-header">
        <div className="container">
          <h1>Our Story</h1>
          <p className="white-text">Building dreams, one property at a time in Sydney's South West</p>
        </div>
      </section>

      {/* Abhi's Story Section */}
      <section className="story-section">
        <div className="container">
          <div className="story-content">
            <div className="story-text">
              <h2>Meet Abhi</h2>
              <h3 className="story-subtitle">Sales Director</h3>
              <div className="story-bio">
                <p>
                  Hi, I'm Abhi, Sales Director with over 18 years of experience in sales across Sydney's South West. Since moving to the area in 2006, I've developed a strong passion for helping people find their dream homes and investment opportunities.
                </p>
                <p>
                  Whether it's a family home or a commercial property, I pride myself on providing honest advice, local insights, and results that truly make a difference. I love showcasing everything that makes our community—from Austral, Leppington, Catherine field, Oran Park, Edmondson Park, Gregory hills, Figtree hill, Wilton, Gledswood hills and many more—such a fantastic place to live and invest.
                </p>
                <p>
                  As a licensed real estate agent with extensive experience in Sydney's South West, I know the local market inside out and have helped countless clients buy, sell, and build their homes. If you're looking to make a move or simply need expert advice, I'm here to help—contact me today!
                </p>
              </div>
              <div className="story-cta">
                <a href="/contact" className="cta-button">Get In Touch</a>
              </div>
            </div>
            <div className="story-image">
              <img src={AbhiPhoto} alt="Abhi - Sales Director at AuzLandRE" />
            </div>
          </div>
        </div>
      </section>
      {/* Services Section */}
      <section
        id="services-section"
        ref={setSectionRef('services-section')}
        className={`section services-section ${pageLoaded ? 'animate-in' : ''}`}
      >
        <div className="container">
          <h2 className={`section-title handwriting-font ${pageLoaded ? 'animate-title' : ''}`}>
            Explore All Things Property
          </h2>
          <div className={`services-grid ${pageLoaded ? 'animate-grid' : ''}`}>
            <div className="service-card card">
              <div className="service-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 11H15M9 15H15M17 21H7C5.89543 21 5 20.1046 5 19V5C5 3.89543 5.89543 3 7 3H12.5858C12.851 3 13.1054 3.10536 13.2929 3.29289L19.7071 9.70711C19.8946 9.89464 20 10.149 20 10.4142V19C20 20.1046 19.1046 21 18 21H17ZM17 21V10H12V5H7V19H17Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Get Estimated Property Prices</h3>
              <p>See how much your property's worth whether you own it or want to buy it.</p>
              <button className="btn btn-primary" onClick={openAppraisalOverlay}>Check Property Values</button>
            </div>
            
            <div className="service-card card">
              <div className="service-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M12 2L2 7L12 12L22 7L12 2Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 17L12 22L22 17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M2 12L12 17L22 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Need Help with a Mortgage?</h3>
              <p>Compare your finance options to make an informed call.</p>
              <button className="btn btn-primary" onClick={() => { window.open("https://bluemoonfinances.com.au/", "_blank");}}>Explore Home Loans</button>
            </div>
            
            <div className="service-card card">
              <div className="service-icon">
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M9 20L3 17V4L9 1L15 4V17L9 20Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M9 1V20" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  <path d="M15 4V17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
              </div>
              <h3>Explore Suburb Profiles</h3>
              <p>Check out different suburb profiles and find one that's right for you.</p>
              <button className="btn btn-primary" onClick={() => { window.open("https://www.realestate.com.au/australia/", "_blank");}}>Research Suburbs</button>
            </div>
          </div>
        </div>
      </section>
      <RequestAppraisalOverlay isOpen={isAppraisalOverlayOpen} onClose={closeAppraisalOverlay} />
    </div>
  );
};

export default AboutPage; 